import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import {
  pathSteps,
  type OnboardingState,
  type OnboardingStep,
} from '../../../../types/Onboarding'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { onboardingStateLoaded } from '../../stores/OnboardingStore'
import { setAuthenticated, setUserData } from '../../stores/UserStore'
import authService from '../../services/authService'
import { advanceOnboarding, goBackOnboarding, refreshOnboardingState } from '../../services/OnboardingService'
import { trackEvent } from '../../services/analyticsService'
import CompanyStep from './steps/CompanyStep'
import JoinCompanyStep from './steps/JoinCompanyStep'
import CardStep from './steps/CardStep'
import SevvSelfStep from './steps/SevvSelfStep'
import InviteStep from './steps/InviteStep'

// Its own chunk: the walk's code and images aren't needed by anyone who isn't onboarding.
const GuidedWalk = lazy(() => import('./walk/GuidedWalk'))

type ActiveState = Extract<OnboardingState, { active: true }>

const STEP_TITLES: Record<OnboardingStep, string> = {
  company: 'Create your office',
  join: 'Join your office',
  card: 'Start your free trial',
  self: 'Your Sevv self',
  invite: 'Invite your team',
  walk: 'Guided walk',
}

/**
 * The onboarding wizard (SC-438). AppController shows it instead of the menu while the signed-in
 * user's onboarding is active. The cloud decides the step; this component only shows it and
 * reports when the person finishes it, so a reload or a second device lands on the same step.
 *
 * Each step's screen is built in its own phase of the onboarding plan; until then a step shows a
 * placeholder.
 */
const OnboardingWizard: React.FC = () => {
  const dispatch = useAppDispatch()
  const userId = useAppSelector((s) => s.onboarding.userId)
  const state = useAppSelector((s) => s.onboarding.state)
  const notice = useAppSelector((s) => s.onboarding.notice)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [signingOut, setSigningOut] = useState(false)
  /** The last error came from checkAgain, so the step that called it is still showing its busy state. */
  const [checkFailed, setCheckFailed] = useState(false)
  const startedTracked = useRef(false)

  useEffect(() => {
    if (state.active && !startedTracked.current) {
      startedTracked.current = true
      trackEvent('APP_onboarding_started', { onboarding_trigger: 'wizard', step: state.step })
    }
  }, [state])

  if (!state.active || !userId) return null

  const apply = (next: OnboardingState) => dispatch(onboardingStateLoaded({ userId, state: next }))

  const finishStep = async (step: OnboardingStep) => {
    setBusy(true)
    setError(null)
    setCheckFailed(false)
    try {
      const next = await advanceOnboarding(step)
      trackEvent('APP_onboarding_step_completed', { step })
      if (!next.active) trackEvent('APP_onboarding_completed')
      apply(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const goBack = async (step: OnboardingStep) => {
    setBusy(true)
    setError(null)
    setCheckFailed(false)
    try {
      apply(await goBackOnboarding(step))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * For steps that finish on their own data (company, card): ask the cloud where we are now. The
   * step has already done its work and stays busy, as running it again would make a second office
   * or trial, so a failed check offers its own retry.
   */
  const checkAgain = async () => {
    setBusy(true)
    setError(null)
    setCheckFailed(false)
    try {
      apply(await refreshOnboardingState())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.')
      setCheckFailed(true)
    } finally {
      setBusy(false)
    }
  }

  // The way out for someone who can't finish (their company is full, or they used the wrong
  // account). Same sequence as the Terms screen: the Redux clear runs even if signOut throws, and
  // the onboarding gate resets once the user is gone.
  const signOut = async () => {
    setSigningOut(true)
    try {
      await authService.signOut()
    } catch (err) {
      console.error('[OnboardingWizard] Sign out failed; clearing the local session anyway:', err)
    } finally {
      dispatch(setUserData(null))
      dispatch(setAuthenticated(false))
    }
  }

  const steps = pathSteps(state.path)
  const position = steps.indexOf(state.step) + 1

  return (
    // The app shell never scrolls the page (the game owns the viewport), so the wizard is its own
    // full-screen scroll container. `margin: auto` on the card centres a short step and lets a
    // long one start at the top instead of being cut off above.
    <Box
      data-testid="onboarding-wizard-scroll"
      sx={{
        position: 'fixed',
        inset: 0,
        overflowY: 'auto',
        display: 'flex',
        px: 2,
        py: 4,
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{ width: '100%', maxWidth: 930, m: 'auto', borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary" data-testid="onboarding-step-header">
            Step {position} of {steps.length} · <b>{STEP_TITLES[state.step]}</b>
          </Typography>
          <Button
            size="small"
            onClick={() => void signOut()}
            disabled={signingOut}
            data-testid="onboarding-sign-out"
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Sign out
          </Button>
        </Box>
        <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
          {notice && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {notice}
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={checkFailed ? undefined : () => setError(null)}
              action={
                checkFailed ? (
                  <Button color="inherit" size="small" disabled={busy} onClick={() => void checkAgain()}>
                    Try again
                  </Button>
                ) : undefined
              }
            >
              {error}
            </Alert>
          )}
          <StepScreen
            state={state}
            busy={busy}
            onFinish={finishStep}
            onBack={goBack}
            onCheckAgain={checkAgain}
            error={error}
            header={`Step ${position} of ${steps.length} · ${STEP_TITLES[state.step]}`}
          />
        </Box>
      </Paper>
    </Box>
  )
}

interface StepScreenProps {
  state: ActiveState
  busy: boolean
  onFinish: (step: OnboardingStep) => void
  onBack: (step: OnboardingStep) => void
  onCheckAgain: () => void
  /** For the walk, which covers the card and so shows these itself. */
  error: string | null
  header: string
}

function StepScreen({ state, busy, onFinish, onBack, onCheckAgain, error, header }: StepScreenProps) {
  const email = useAppSelector((s) => s.user.userData?.email)
  const emailDomain = email?.split('@')[1]?.toLowerCase() || null
  switch (state.step) {
    case 'company':
      // Creating the company finishes this step: the cloud then answers with the card step.
      return <CompanyStep creatorEmail={email} onCreated={onCheckAgain} />
    case 'join':
      return (
        <JoinCompanyStep
          company={state.company}
          emailDomain={emailDomain}
          busy={busy}
          onJoin={() => onFinish('join')}
        />
      )
    case 'card':
      // Starting the trial finishes this step: the cloud then answers with the next one.
      return state.company ? (
        <CardStep companyId={state.company.id} onTrialStarted={onCheckAgain} />
      ) : (
        <Placeholder title={STEP_TITLES.card} text="Your company is still being set up.">
          <Button variant="outlined" disabled={busy} onClick={onCheckAgain}>
            Check again
          </Button>
        </Placeholder>
      )
    case 'self':
      return <SevvSelfStep busy={busy} onSaved={() => onFinish('self')} />
    case 'invite':
      // Only this step has a Back: "Your Sevv self" left nothing behind that going back would undo.
      return state.company ? (
        <InviteStep
          companyId={state.company.id}
          busy={busy}
          onDone={() => onFinish('invite')}
          onBack={() => onBack('invite')}
        />
      ) : (
        <Placeholder title={STEP_TITLES.invite} text="Your company is still being set up.">
          <Button variant="outlined" disabled={busy} onClick={onCheckAgain}>
            Check again
          </Button>
        </Placeholder>
      )
    case 'walk':
      return (
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          }
        >
          <GuidedWalk busy={busy} error={error} header={header} onFinish={() => onFinish('walk')} />
        </Suspense>
      )
    default:
      return (
        <Placeholder title={STEP_TITLES[state.step]}>
          <Button variant="contained" disabled={busy} onClick={() => onFinish(state.step)}>
            Continue
          </Button>
        </Placeholder>
      )
  }
}

function Placeholder({
  title,
  text,
  children,
}: {
  title: string
  text?: string
  children: React.ReactNode
}) {
  return (
    <Stack spacing={2} alignItems="flex-start">
      <Typography variant="h5" component="h1" fontWeight={700}>
        {title}
      </Typography>
      {text && <Typography color="text.secondary">{text}</Typography>}
      {children}
    </Stack>
  )
}

export default OnboardingWizard
